import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Api } from "../lib/api";
import { auth } from "../firebase";
import firebase from "firebase/compat/app";
import "./LoginPage.css";
import { syncAuthUserToFirestore } from "../lib/syncAuthUserToFirestore";

const RESEND_SECONDS = 60;

const Register = () => {
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [stage, setStage] = useState("register");

  const [code, setCode] = useState("");
  const [mfaTxId, setMfaTxId] = useState(null);
  const [codeVia, setCodeVia] = useState(null);

  const [rememberMe, setRememberMe] = useState(false);

  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const [info, setInfo] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    if (stage !== "code" || resendCountdown <= 0) return;

    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [stage, resendCountdown]);

  const startResendTimer = () => {
    setResendCountdown(RESEND_SECONDS);
  };

  const friendlyError = (err) => {
    const msg = String(err?.message || "").toLowerCase();

    if (msg.includes("password")) return "Your password must be at least 6 characters long.";
    if (msg.includes("valid email") || msg.includes("valid phone")) return "Please enter a valid email address or phone number.";
    if (msg.includes("network") || msg.includes("fetch")) return "We could not reach the server. Please check your internet connection and try again.";
    if (msg.includes("expired")) return "Your verification code has expired. Please request a new one.";
    if (msg.includes("6-digit")) return "Please enter the 6-digit verification code.";
    if (msg.includes("verification")) return "We could not verify your code. Please try again.";
    if (msg.includes("session")) return "Your verification session has expired. Please restart registration.";

    return "Something went wrong. Please try again.";
  };

  const detectMode = (input) => {
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)) return "email";
    if (/^\d{7,13}$/.test(input) || /^\+\d{10,15}$/.test(input)) return "phone";
    return "unknown";
  };

  const normalizePhone = (input) => {
    let phone = (input || "").trim();
    if (!phone.startsWith("+")) {
      phone = phone.startsWith("0") ? "+27" + phone.slice(1) : "+27" + phone;
    }
    return phone;
  };

  const clearCodeState = () => {
    setCode("");
    setMfaTxId(null);
    setCodeVia(null);
    setResendCountdown(0);
  };

  const changeIdentifier = () => {
    setStage("register");
    clearCodeState();
    setInfo("");
    setError("");
  };

  const afterAuthSuccess = async () => {
    try {
      await auth.currentUser?.reload();
    } catch {}

    try {
      await Api.meOptional();
    } catch {}

    navigate("/preferences", {
      replace: true,
      state: { onboarding: true, fromRegister: true },
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");

    const raw = emailOrPhone.trim();
    const mode = detectMode(raw);

    try {
      if (mode === "unknown") throw new Error("Please enter a valid email or phone number.");
      if (!password || password.length < 6) throw new Error("Password must be at least 6 characters.");

      const base =
        mode === "email"
          ? { email: raw.toLowerCase(), password: password.trim(), rememberMe }
          : { phone: normalizePhone(raw), password: password.trim(), rememberMe };

      const r1 = await Api.register(base);
      if (!r1?.success) throw new Error("Registration failed.");

      const r2 = await Api.loginPassword(base);
      if (!r2?.mfa?.txId || !r2?.mfa?.via) throw new Error("Could not start verification.");

      setMfaTxId(r2.mfa.txId);
      setCodeVia(r2.mfa.via === "phone" ? "phone" : "email");
      setStage("code");
      startResendTimer();

      setInfo(
        r2.mfa.via === "phone"
          ? "We sent a verification code to your phone."
          : "We sent a verification code to your email."
      );
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");

    try {
      if (!/^\d{6}$/.test(code.trim())) throw new Error("Enter the 6-digit code.");
      if (!mfaTxId) throw new Error("Session expired. Please restart registration.");

      const r = await Api.verifyMfa({ txId: mfaTxId, code: code.trim() });
      if (!r?.success) throw new Error("Verification failed.");

      const token = r?.firebase?.customToken;
      if (!token) throw new Error("Login failed.");

      await auth.setPersistence(
        rememberMe
          ? firebase.auth.Auth.Persistence.LOCAL
          : firebase.auth.Auth.Persistence.SESSION
      );

      const cred = await auth.signInWithCustomToken(token);
      await syncAuthUserToFirestore(cred.user);

      setInfo("Your account has been created successfully.");
      await afterAuthSuccess();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) return;

    const raw = emailOrPhone.trim();
    const mode = detectMode(raw);

    setResending(true);
    setError("");
    setInfo("");

    try {
      const base =
        mode === "email"
          ? { email: raw.toLowerCase(), password: password.trim(), rememberMe }
          : { phone: normalizePhone(raw), password: password.trim(), rememberMe };

      const r = await Api.loginPassword(base);
      if (!r?.mfa?.txId || !r?.mfa?.via) throw new Error("Unable to resend code.");

      setMfaTxId(r.mfa.txId);
      setCodeVia(r.mfa.via === "phone" ? "phone" : "email");
      startResendTimer();

      setInfo(
        r.mfa.via === "phone"
          ? "A new code has been sent to your phone."
          : "A new code has been sent to your email."
      );
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setResending(false);
    }
  };

  const linkButtonStyle = {
    background: "transparent",
    border: "none",
    color: "#2457a6",
    padding: "6px 0",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 700,
    minHeight: 36,
  };

  const isCodeStage = stage === "code";

  return (
    <div className="login-page-wrapper">
      <div className="login-page-shell">
        
        <div className="login-page-container login-page-container--premium auth-container">
          <aside className="login-left login-left--premium">
            <div className="login-left__badge">Secure account access</div>

            <div className="login-brand login-brand--centered">
              <img src="/handshake-logo.png" alt="NexterZone" className="login-logo" />
              <h1 className="login-brand__title">Nexter Zone</h1>
              <p className="slogan">Where value finds its match.</p>
            </div>

            <div className="login-left__content">
              <h2 className="login-left__headline">
                Create your account and start your marketplace journey.
              </h2>

              <p className="login-left__copy">
                Join securely, save your preferences, discover relevant trades, and manage your offers and activity from one place.
              </p>

              <div className="login-trust-list">
                <div className="login-trust-item login-trust-item--with-icon">
                  <span className="login-trust-icon">✓</span>
                  <span>
                    <span className="login-trust-item__title">Secure verification</span>
                    <span className="login-trust-item__text">Email or phone registration with verification support.</span>
                  </span>
                </div>

                <div className="login-trust-item login-trust-item--with-icon">
                  <span className="login-trust-icon">●</span>
                  <span>
                    <span className="login-trust-item__title">Easy onboarding</span>
                    <span className="login-trust-item__text">Complete setup quickly and continue straight to your preferences.</span>
                  </span>
                </div>

                <div className="login-trust-item login-trust-item--with-icon">
                  <span className="login-trust-icon">↗</span>
                  <span>
                    <span className="login-trust-item__title">Ready to trade</span>
                    <span className="login-trust-item__text">Begin browsing, posting, and managing trades with confidence.</span>
                  </span>
                </div>
              </div>
            </div>
          </aside>

          <section className="login-right login-right--premium">
            <div className="login-panel">
              <div className="login-panel__header">
                <div className="login-panel__eyebrow">
                  {isCodeStage ? "Verification required" : "Welcome"}
                </div>

                <h2>{isCodeStage ? "Verify your account" : "Create Account"}</h2>

                <p className="login-panel__subtext">
                  {isCodeStage
                    ? `Enter the 6-digit ${codeVia === "phone" ? "SMS" : "email"} code to finish creating your account.`
                    : "Use your email or phone number to create your account."}
                </p>
              </div>

              {error && <div className="error-message">{error}</div>}
              {info && <div className="info-message">{info}</div>}

              {stage === "register" ? (
                <form onSubmit={handleRegister} autoComplete="off" className="login-form">
                  <div className="login-field">
                    <label htmlFor="register-identifier">Email or Phone</label>
                    <input
                      id="register-identifier"
                      type="text"
                      placeholder="Enter your email or phone"
                      autoComplete="username"
                      value={emailOrPhone}
                      onChange={(e) => setEmailOrPhone(e.target.value)}
                      required
                    />
                  </div>

                  <div className="login-field">
                    <label htmlFor="register-password">Password</label>
                    <input
                      id="register-password"
                      type="password"
                      placeholder="Create a password (min 6 characters)"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>

                  <label className="remember-me-label login-check-row">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <span>Remember me</span>
                  </label>

                  <button type="submit" disabled={loading} className="login-primary-btn">
                    {loading ? "Creating..." : "Create Account"}
                  </button>

                  <p className="login-switch-mode">
                    Already have an account? <Link to="/login">Login</Link>
                  </p>
                </form>
              ) : (
                <form onSubmit={handleVerify} autoComplete="off" className="login-form">
                  <div className="login-field">
                    <label htmlFor="register-code">Verification code</label>
                    <input
                      id="register-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder={`Enter the 6-digit ${codeVia === "phone" ? "SMS" : "email"} code`}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                  </div>

                  <div className="login-form__row login-form__row--split">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resending || resendCountdown > 0}
                      style={{
                        ...linkButtonStyle,
                        color: resendCountdown > 0 ? "#9aa3af" : "#2457a6",
                        cursor: resendCountdown > 0 ? "not-allowed" : "pointer",
                      }}
                    >
                      {resending
                        ? "Sending new code..."
                        : resendCountdown > 0
                        ? `Resend code in ${resendCountdown}s`
                        : "Resend code"}
                    </button>

                    <button
                      type="button"
                      onClick={changeIdentifier}
                      style={{ ...linkButtonStyle, color: "#64748b" }}
                    >
                      Change email or phone
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || code.trim().length !== 6}
                    className="login-primary-btn"
                  >
                    {loading ? "Verifying..." : "Verify & Finish"}
                  </button>

                  <p className="login-switch-mode">
                    Already have an account? <Link to="/login">Login</Link>
                  </p>
                </form>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Register;